import { create } from 'zustand';
import { purchaseOrderService } from '../services/purchaseOrderService';
import { getApiErrorMessage } from '@/lib/getApiErrorMessage';
import type {
  PurchaseOrderDto,
  PurchaseOrderSyncResultDto,
} from '../types/purchaseOrder.types';

interface PagedParams {
  page: number;
  pageSize: number;
  sortBy?: string;
  sortDesc?: boolean;
}

interface PurchaseOrderState {
  // List state
  purchaseOrders: PurchaseOrderDto[];
  isLoading: boolean;
  error: string | null;

  // Paged list state
  totalCount: number;
  totalPages: number;
  isLoadingPaged: boolean;
  pagedError: string | null;

  // Sync state
  isSyncing: boolean;
  lastSyncResult: PurchaseOrderSyncResultDto | null;
  syncError: string | null;

  // Detail page state
  selectedPurchaseOrder: PurchaseOrderDto | null;
  isLoadingDetail: boolean;
  detailError: string | null;

  // Actions
  fetchAll: () => Promise<void>;
  fetchAllPaged: (params: PagedParams) => Promise<void>;
  syncFromNetSuite: (syncFromDate?: string) => Promise<PurchaseOrderSyncResultDto | null>;
  resyncPurchaseOrder: (id: number) => Promise<PurchaseOrderSyncResultDto | null>;
  fetchPurchaseOrderById: (id: number) => Promise<void>;
  clearSelectedPurchaseOrder: () => void;
  clearError: () => void;
  clearSyncError: () => void;
}

export const usePurchaseOrderStore = create<PurchaseOrderState>()((set) => ({
  purchaseOrders: [],
  isLoading: false,
  error: null,

  totalCount: 0,
  totalPages: 0,
  isLoadingPaged: false,
  pagedError: null,

  isSyncing: false,
  lastSyncResult: null,
  syncError: null,

  selectedPurchaseOrder: null,
  isLoadingDetail: false,
  detailError: null,

  fetchAll: async () => {
    set({ isLoading: true, error: null });
    try {
      const purchaseOrders = await purchaseOrderService.getAll();
      set({ purchaseOrders, isLoading: false });
    } catch (error) {
      set({
        error: error instanceof Error ? error.message : 'Failed to fetch POs',
        isLoading: false,
      });
    }
  },

  fetchAllPaged: async (params: PagedParams) => {
    set({ isLoadingPaged: true, pagedError: null });
    try {
      const result = await purchaseOrderService.getAllPaged(params);
      set({
        purchaseOrders: result.items,
        totalCount: result.totalCount,
        totalPages: result.totalPages,
        isLoadingPaged: false,
      });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to fetch purchase orders');
      set({ pagedError: message, isLoadingPaged: false });
    }
  },

  syncFromNetSuite: async (syncFromDate?: string) => {
    set({ isSyncing: true, syncError: null });
    try {
      const result = await purchaseOrderService.syncFromNetSuite(syncFromDate);
      set({ lastSyncResult: result, isSyncing: false });
      return result;
    } catch (error) {
      set({
        syncError:
          error instanceof Error ? error.message : 'Failed to sync POs',
        isSyncing: false,
      });
      return null;
    }
  },

  resyncPurchaseOrder: async (id: number) => {
    try {
      const result = await purchaseOrderService.resync(id);
      return result;
    } catch {
      return null;
    }
  },

  fetchPurchaseOrderById: async (id: number) => {
    set({ isLoadingDetail: true, detailError: null });
    try {
      const purchaseOrder = await purchaseOrderService.getById(id);
      set({ selectedPurchaseOrder: purchaseOrder, isLoadingDetail: false });
    } catch (error) {
      const message = getApiErrorMessage(error, 'Failed to fetch purchase order');
      set({ detailError: message, isLoadingDetail: false });
    }
  },

  clearSelectedPurchaseOrder: () =>
    set({ selectedPurchaseOrder: null, detailError: null }),

  clearError: () => set({ error: null }),
  clearSyncError: () => set({ syncError: null }),
}));
